import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import OptimizedImage from '../components/OptimizedImage';
import PhotoStrip from '../components/PhotoStrip';
import { RESULTS_2026, SEASON_2026 } from '../config/results2026';
import { getActiveSeason, getSeasonState, getNextRegistration } from '../config/seasons';

const S = SEASON_2026;

const RANK_LABEL = { 1: '1st', 2: '2nd', 3: '3rd' };
const ordinal = (n) => RANK_LABEL[n] || `${n}th`;

const Stat = ({ value, label }) => (
  <div style={{ textAlign: 'center', minWidth: 0 }}>
    <div style={{
      fontFamily: "'Playfair Display', Georgia, serif",
      fontSize: 'clamp(2rem, 7vw, 2.75rem)',
      lineHeight: 1,
      color: 'var(--primary-gold)',
      fontWeight: 700,
    }}>{value}</div>
    <div style={{
      fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase',
      color: 'var(--soft-gray)', marginTop: '0.4rem', fontWeight: 600,
    }}>{label}</div>
  </div>
);

const DivisionResults = ({ group }) => (
  <div style={{ marginBottom: '2.5rem' }}>
    <div style={{
      display: 'flex', alignItems: 'baseline', gap: '0.75rem', flexWrap: 'wrap',
      paddingBottom: '0.6rem', borderBottom: '2px solid var(--primary-gold)', marginBottom: '0.25rem',
    }}>
      <h3 style={{ margin: 0, fontSize: '1.05rem', color: 'var(--charcoal)' }}>
        {group.division}
      </h3>
      <span style={{ fontSize: '0.82rem', color: 'var(--soft-gray)' }}>
        {group.category} · {group.entrants} {group.entrants === 1 ? 'dancer' : 'dancers'}
      </span>
    </div>

    {group.placements.map((p) => (
      <div
        key={p.rank}
        style={{
          display: 'grid',
          // Rank column is fixed so every row across every division lines up;
          // name and variation share what's left and stack on a narrow screen.
          gridTemplateColumns: '2.6rem minmax(9rem, 1fr) minmax(0, 1.4fr)',
          gap: '0.25rem 0.9rem',
          alignItems: 'baseline',
          padding: '0.55rem 0',
          borderBottom: '1px solid rgba(0,0,0,0.06)',
        }}
      >
        <span style={{
          fontWeight: 700, fontSize: '0.85rem',
          color: p.rank === 1 ? 'var(--deep-gold)' : 'var(--soft-gray)',
        }}>
          {ordinal(p.rank)}
        </span>
        <span style={{
          color: 'var(--charcoal)', fontSize: '0.95rem',
          fontWeight: p.rank === 1 ? 700 : 600,
        }}>
          {p.dancer}
        </span>
        <span style={{ color: 'var(--soft-gray)', fontSize: '0.88rem', lineHeight: 1.4 }}>
          {p.variation}
        </span>
      </div>
    ))}
  </div>
);

const Season2026 = () => {
  // The season after this one: open now, or the next one to open.
  const active = getActiveSeason();
  const nextOpen = active.year > S.year && getSeasonState(active.year) === 'open';
  const next = getNextRegistration();
  const nextYear = nextOpen ? active.year : next.year;

  return (
    <Layout
      title={`${S.year} Results & Gallery | Prix du Canada`}
      description={`Results, placements and photographs from the inaugural Prix du Canada, held ${S.eventDate} at the ${S.venue} in Toronto.`}
      keywords="Prix du Canada 2026 results, ballet competition results Toronto, ballet competition winners Canada"
    >
      {/* Hero — the one photo that loads eagerly on this page */}
      <section style={{ position: 'relative' }}>
        <OptimizedImage
          slug="competitive-junior-award-ceremony-holding-certificates-all"
          alt={`Junior competitors with their certificates at the ${S.year} award ceremony`}
          sizes="100vw"
          priority
          style={{ maxHeight: '60vh' }}
          // Shifts the visible window down the photo, which lifts the image
          // up in the frame and brings the stage and the dancers' feet into
          // view. Higher % = more of the bottom of the photo.
          objectPosition="center 72%"
        />
        {/* Scrim is bottom-weighted rather than a flat wash: this is a group
            photo, and darkening it evenly to make white text readable would
            put a grey veil over everyone's faces. */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0) 35%, rgba(0,0,0,0.25) 60%, rgba(0,0,0,0.78) 100%)',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'flex-end',
          padding: '2rem 1rem', textAlign: 'center',
        }}>
          <h1 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            color: 'white', margin: 0,
            fontSize: 'clamp(1.6rem, 6vw, 3rem)', lineHeight: 1.15,
          }}>
            Prix du Canada {S.year}
          </h1>
          <p style={{
            color: 'rgba(255,255,255,0.92)', margin: '0.6rem 0 0',
            fontSize: 'clamp(0.9rem, 3.2vw, 1.1rem)',
          }}>
            {S.eventDate} · {S.venue}, Toronto
          </p>
        </div>
      </section>

      {/* Season at a glance */}
      <section className="section" style={{ paddingTop: '2.5rem', paddingBottom: '2.5rem' }}>
        <div className="container">
          {/* Three across at every width. Wrapping put 2 + 1 on a phone,
              which read as a mistake rather than a layout. */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
            justifyItems: 'center', alignItems: 'start',
            gap: '0.5rem', marginBottom: '2.5rem',
          }}>
            <Stat value={S.dancers} label="Dancers" />
            <Stat value={S.variations} label="Variations" />
            <Stat value={S.divisions} label="Divisions" />
          </div>

          <p style={{
            textAlign: 'center', maxWidth: '46rem', margin: '0 auto',
            color: 'var(--soft-gray)', fontSize: '1.05rem', lineHeight: 1.75,
          }}>
            The inaugural Prix du Canada took place on {S.eventDate} at the {S.venue},{' '}
            {S.venueDetail}. Dancers aged seven through adult competed across {S.divisions} divisions
            in classical variation, adjudicated by a panel of three. Every participant received a
            written report from the judging panel and a professional performance video.
          </p>
        </div>
      </section>

      {/* Placements */}
      <section className="section" id="results" style={{ background: 'var(--light-gray)' }}>
        <div className="container">
          <h2 style={{ textAlign: 'center' }}>Results</h2>
          <p style={{
            textAlign: 'center', color: 'var(--soft-gray)',
            maxWidth: '40rem', margin: '0 auto 2.5rem', fontSize: '1rem', lineHeight: 1.7,
          }}>
            Placements as announced at the award ceremony. The number of placements awarded
            varies by division according to the number of dancers competing.
          </p>

          <div style={{ maxWidth: '820px', margin: '0 auto' }}>
            {RESULTS_2026.map((g) => <DivisionResults key={g.key} group={g} />)}
          </div>

          <p style={{
            textAlign: 'center', color: 'var(--soft-gray)',
            fontSize: '0.9rem', marginTop: '2.5rem',
          }}>
            Competitors: your full judging report is ready. Log in to your{' '}
            <Link to="/profile" style={{ color: 'var(--primary-gold)', fontWeight: 600 }}>
              dancer profile
            </Link>{' '}
            and open it under &ldquo;Past Competitions&rdquo;.
          </p>
        </div>
      </section>

      {/* Gallery — the competition as it happened */}
      <section className="section" id="gallery">
        <div className="container">
          <h2 style={{ textAlign: 'center' }}>The Day</h2>
          <p style={{
            textAlign: 'center', color: 'var(--soft-gray)',
            maxWidth: '40rem', margin: '0 auto 2.5rem', fontSize: '1rem', lineHeight: 1.7,
          }}>
            Photographs from the {S.year} competition.
          </p>

          <PhotoStrip
            photos={[
              ['empty-stage-with-pdc-logo-projection', 'The stage before the competition began'],
              ['adult-rec-first-place-dancer-high-alasecond', 'An adult recreational dancer on stage'],
              ['junior-dancer-beautiful-penche', 'A junior dancer in penché'],
              ['adult-competitive-with-nice-portdebra', 'An adult competitive dancer, port de bras'],
              ['young-dancer-with-perfect-arabesque', 'A young dancer in arabesque'],
              ['adult-rec-dancer-on-pointe-arabesque', 'An adult recreational dancer in arabesque on pointe'],
              ['adult-competitive-dancer-beautiful-attitude', 'An adult competitive dancer in attitude'],
              ['adult-rec-dancer-esmeralda-ending-pose', 'Final pose of an Esmeralda variation'],
              ['adult-dancers-gather-backstage-before-curtain', 'Dancers gathered backstage before the curtain'],
            ]}
          />
        </div>
      </section>

      {/* Award ceremony */}
      <section className="section" style={{ background: 'var(--light-gray)' }}>
        <div className="container">
          <h2 style={{ textAlign: 'center' }}>The Award Ceremony</h2>
          <p style={{
            textAlign: 'center', color: 'var(--soft-gray)',
            maxWidth: '40rem', margin: '0 auto 2.5rem', fontSize: '1rem', lineHeight: 1.7,
          }}>
            Medals and certificates were presented at the close of each session.
          </p>
          <PhotoStrip
            photos={[
              ['youngest-division-kids-smile-award-ceremony', 'The youngest division at the award ceremony'],
              ['junior-dancer-first-place-award-announcement', 'A first place announcement in the junior division'],
              ['adult-rec-award-ceremony-all', 'The adult recreational division at the award ceremony'],
              ['pdc-medals-in-nice-tray', 'Prix du Canada medals'],
              ['award-certificate-tray-close-up', 'Prix du Canada certificates'],
              ['judge-giving-a-few-words-to-junior-dancers', 'A judge speaking with the junior dancers'],
            ]}
          />
        </div>
      </section>

      {/* Judging panel — moved here from /competition, which is now evergreen */}
      <section className="section" id="judges">
        <div className="container">
          <h2 style={{textAlign: 'center'}}>The {S.year} Judging Panel</h2>
          <p style={{textAlign: 'center', fontSize: '1.15rem', color: 'var(--soft-gray)', maxWidth: '720px', margin: '0 auto 3rem', lineHeight: '1.7'}}>
            Three distinguished artists adjudicated the inaugural Prix du Canada. Between them they brought decades of performance, teaching, and creative leadership from some of the world's most respected ballet institutions.
          </p>

          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem', marginTop: '2rem'}}>

            {/* Judge 1 — Cynthia Macedo */}
            <div className="card" style={{textAlign: 'center', padding: '2rem 1.5rem', display: 'flex', flexDirection: 'column', height: '100%'}}>
              <img
                src="https://oabvutyzjxtmoahuxdjg.supabase.co/storage/v1/object/public/email-images/judges/cynthia_macedo.jpg"
                alt="Cynthia Macedo"
                style={{width: '160px', height: '160px', borderRadius: '50%', margin: '0 auto 1.25rem', objectFit: 'cover', display: 'block', border: '3px solid var(--primary-gold)'}}
              />
              <h3 style={{fontSize: '1.4rem', marginBottom: '0.4rem', color: 'var(--charcoal)'}}>Cynthia Macedo</h3>
              <p style={{fontSize: '0.9rem', margin: '0 0 1.25rem 0', color: 'var(--primary-gold)', fontStyle: 'italic'}}>Former Soloist, The National Ballet of Canada</p>
              <p style={{fontSize: '0.98rem', margin: '0', lineHeight: '1.7', textAlign: 'left', color: 'var(--charcoal)'}}>
                Cynthia entered the National Ballet School of Canada at age ten under Betty Oliphant and joined the National Ballet of Canada as a soloist (1982–1994), performing under Erik Bruhn, Rudolf Nureyev, Natalia Makarova, Celia Franca, and Glen Tetley. Today she serves as company teacher and rehearsal coach with Canada's Ballet Jörgen, full-time ballet instructor at George Brown College, and faculty at the National Ballet of Canada's InStudio. She has adjudicated numerous competitions across Canada.
              </p>
            </div>

            {/* Judge 2 — Sarahí Cárdenas */}
            <div className="card" style={{textAlign: 'center', padding: '2rem 1.5rem', display: 'flex', flexDirection: 'column', height: '100%'}}>
              <img
                src="https://oabvutyzjxtmoahuxdjg.supabase.co/storage/v1/object/public/email-images/judges/sarahi.jpg"
                alt="Sarahí Cárdenas"
                style={{width: '160px', height: '160px', borderRadius: '50%', margin: '0 auto 1.25rem', objectFit: 'cover', display: 'block', border: '3px solid var(--primary-gold)'}}
              />
              <h3 style={{fontSize: '1.4rem', marginBottom: '0.4rem', color: 'var(--charcoal)'}}>Sarahí Cárdenas</h3>
              <p style={{fontSize: '0.9rem', margin: '0 0 1.25rem 0', color: 'var(--primary-gold)', fontStyle: 'italic'}}>Artistic Director · Educator · Curator</p>
              <p style={{fontSize: '0.98rem', margin: '0', lineHeight: '1.7', textAlign: 'left', color: 'var(--charcoal)'}}>
                Originally from Saltillo, Mexico, Sarahí is a dance artist, educator, curator, and advocate shaping Toronto's dance landscape. She trained at Escuela Superior de Música y Danza de Monterrey and The Rock School for Dance Education before performing with Ballet Metropolitano de Monterrey, Ballet de Monterrey, Canada's Ballet Jörgen, and the Canadian Opera Company. Sarahí is co-founder and director of The Fifth Dance and founder of Casa de Danza, and serves as contract faculty at York University, InStudio, and George Brown College.
              </p>
            </div>

            {/* Judge 3 — Vlad Romashchenko */}
            <div className="card" style={{textAlign: 'center', padding: '2rem 1.5rem', display: 'flex', flexDirection: 'column', height: '100%'}}>
              <img
                src="https://oabvutyzjxtmoahuxdjg.supabase.co/storage/v1/object/public/email-images/judges/vlad_Romashchenko.jpeg"
                alt="Vlad Romashchenko"
                style={{width: '160px', height: '160px', borderRadius: '50%', margin: '0 auto 1.25rem', objectFit: 'cover', display: 'block', border: '3px solid var(--primary-gold)'}}
              />
              <h3 style={{fontSize: '1.4rem', marginBottom: '0.4rem', color: 'var(--charcoal)'}}>Vlad Romashchenko</h3>
              <p style={{fontSize: '0.9rem', margin: '0 0 1.25rem 0', color: 'var(--primary-gold)', fontStyle: 'italic'}}>Guest Principal, Syracuse City Ballet</p>
              <p style={{fontSize: '0.98rem', margin: '0', lineHeight: '1.7', textAlign: 'left', color: 'var(--charcoal)'}}>
                Born in Kyiv, Ukraine, Vladyslav graduated from the Kyiv Choreographic College in 2011, gained recognition at international competitions, and joined the National Opera of Ukraine as a soloist. Following the 2022 invasion of Ukraine, he joined the Hamburg Ballet, using dance as a means of supporting his country through a difficult time. He later relocated to Toronto, becoming an instructor at InStudio at the National Ballet of Canada. He currently serves as guest principal dancer with Syracuse City Ballet and co-director of Voilà Ballet Studio.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Next season */}
      <section className="section" style={{ background: 'var(--soft-cream)' }}>
        <div className="container" style={{ maxWidth: '44rem', textAlign: 'center' }}>
          <h2>Prix du Canada {nextYear}</h2>
          <p style={{ color: 'var(--soft-gray)', fontSize: '1.05rem', lineHeight: 1.8 }}>
            {nextOpen
              ? <>Registration for {nextYear} is <strong>open now</strong>, with ensembles and early-bird pricing.</>
              : <>Registration for the {nextYear} season opens <strong>{next.opensPhrase}</strong>. Subscribe to hear first.</>}
          </p>
          <Link
            to={nextOpen ? '/register' : '/#newsletter'}
            style={{
              display: 'inline-block', marginTop: '0.5rem', padding: '0.8rem 2rem',
              background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))',
              color: 'white', textDecoration: 'none', borderRadius: '6px', fontWeight: 600,
            }}
          >
            {nextOpen ? `Register for ${nextYear}` : 'Get Registration Updates'}
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default Season2026;
